import React, { useState } from "react";
import axios from "axios";
import { Check, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { REQUEST_INFO } from "../mock";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RequestInfo = () => {
  const F = REQUEST_INFO.form;
  const [form, setForm] = useState({ name: "", phone: "", country: "", message: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.phone.trim()) next.phone = "Please enter a phone number.";
    if (!form.consent) next.consent = "Please confirm you're happy to be contacted.";
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setSending(true);
    setSendError("");
    try {
      await axios.post(`${API}/request-info`, form, { timeout: 30000 });
      try {
        const prev = JSON.parse(localStorage.getItem("cc_requests") || "[]");
        prev.push({ ...form, ts: Date.now() });
        localStorage.setItem("cc_requests", JSON.stringify(prev));
      } catch (_) {}
      setSubmitted(true);
    } catch (err) {
      setSendError("Something went wrong sending your request. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "bg-[#0e0d0b] border-white/10 text-white placeholder:text-[#5f5a51] h-12 rounded-xl focus-visible:ring-[#d69b4e]/40 focus-visible:border-[#d69b4e]/40";

  return (
    <section id="request-info" className="relative py-24 sm:py-28 border-t border-white/[0.05] overflow-hidden scroll-mt-20">
      <div className="absolute inset-x-0 bottom-0 h-[420px] amber-glow rotate-180 opacity-70 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left column */}
          <Reveal>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#d69b4e]" />
              <span className="section-label">{REQUEST_INFO.label}</span>
            </div>
            <h2 className="font-display font-800 text-white text-[2.4rem] sm:text-[3.2rem] leading-[0.98] mt-5 tracking-tight">
              {REQUEST_INFO.titleTop}
              <br />
              <span className="gold-text">{REQUEST_INFO.titleBottom}</span>
            </h2>
            <p className="text-[#a49e92] text-[1.02rem] leading-relaxed mt-6 max-w-md">
              {REQUEST_INFO.body}
            </p>

            <div className="my-8 h-px bg-white/[0.08]" />

            <ul className="space-y-4">
              {REQUEST_INFO.checklist.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#d69b4e]/15 border border-[#d69b4e]/30 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#e8b876]" strokeWidth={3} />
                  </span>
                  <span className="text-[#c7c1b5] text-[0.95rem] leading-snug">{item}</span>
                </li>
              ))}
            </ul>

            <div className="my-8 h-px bg-white/[0.08]" />

            <div>
              <span className="section-label !tracking-[0.2em]">{REQUEST_INFO.sessionsLabel}</span>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3">
                {REQUEST_INFO.sessions.map((day, i) => (
                  <React.Fragment key={day}>
                    {i > 0 && <span className="text-[#5f5a51]">·</span>}
                    <span className="font-display font-700 text-[1.25rem] gold-text">{day}</span>
                  </React.Fragment>
                ))}
              </div>
              <p className="text-[#8a8478] text-[0.9rem] mt-3">{REQUEST_INFO.sessionsNote}</p>
              <p className="text-[#8a8478] text-[0.9rem] mt-4 leading-relaxed max-w-md">
                {REQUEST_INFO.experienceNote}
              </p>
            </div>
          </Reveal>

          {/* Right column - form */}
          <Reveal delay={120}>
            <div className="rounded-[24px] border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 sm:p-8">
              {submitted ? (
                <div className="flex flex-col items-center text-center py-12">
                  <span className="w-16 h-16 rounded-full bg-[#5eae7a]/15 border border-[#5eae7a]/30 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-[#7bd39a]" />
                  </span>
                  <h3 className="font-display font-700 text-white text-[1.5rem] mt-6">{F.successTitle}</h3>
                  <p className="text-[#a49e92] text-[0.95rem] leading-relaxed mt-3 max-w-sm">{F.successBody}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  {/* Name */}
                  <div>
                    <Label className="section-label !tracking-[0.18em] mb-2.5 block">{F.nameLabel}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder={F.namePlaceholder}
                      className={inputClass}
                    />
                    {errors.name && <p className="text-[#f26d6d] text-[0.78rem] mt-1.5">{errors.name}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <Label className="section-label !tracking-[0.18em] mb-2.5 block">{F.phoneLabel}</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder={F.phonePlaceholder}
                      className={inputClass}
                    />
                    {errors.phone ? (
                      <p className="text-[#f26d6d] text-[0.78rem] mt-1.5">{errors.phone}</p>
                    ) : (
                      <p className="text-[#6e685c] text-[0.8rem] mt-2">{F.phoneHelper}</p>
                    )}
                  </div>

                  {/* Country */}
                  <div>
                    <Label className="section-label !tracking-[0.18em] mb-2.5 block">{F.countryLabel}</Label>
                    <Input
                      value={form.country}
                      onChange={(e) => setField("country", e.target.value)}
                      placeholder={F.countryPlaceholder}
                      className={inputClass}
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <Label className="section-label !tracking-[0.18em] mb-2.5 block">{F.messageLabel}</Label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setField("message", e.target.value)}
                      placeholder={F.messagePlaceholder}
                      rows={4}
                      className="bg-[#0e0d0b] border-white/10 text-white placeholder:text-[#5f5a51] rounded-xl resize-none focus-visible:ring-[#d69b4e]/40 focus-visible:border-[#d69b4e]/40"
                    />
                  </div>

                  {/* Consent */}
                  <div className="rounded-xl border border-white/10 bg-[#0e0d0b] p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consent"
                        checked={form.consent}
                        onCheckedChange={(v) => setField("consent", v === true)}
                        className="mt-0.5 border-white/25 data-[state=checked]:bg-[#d69b4e] data-[state=checked]:border-[#d69b4e] data-[state=checked]:text-[#0a0908]"
                      />
                      <Label htmlFor="consent" className="text-[#a49e92] text-[0.85rem] leading-relaxed font-normal cursor-pointer">
                        {F.consent}
                      </Label>
                    </div>
                    {errors.consent && <p className="text-[#f26d6d] text-[0.78rem] mt-2">{errors.consent}</p>}
                  </div>

                  {/* Submit */}
                  {sendError && (
                    <p className="text-[#f26d6d] text-[0.82rem] text-center">{sendError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0a0908] font-600 px-6 py-4 text-[0.95rem] uppercase tracking-wide hover:bg-[#f0ece3] hover:-translate-y-0.5 transition-all duration-200 shadow-[0_12px_36px_-12px_rgba(255,255,255,0.5)] disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        {F.submit} <span aria-hidden>→</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal delay={200} className="text-center mt-12">
          <p className="text-[#8a8478] text-[0.85rem] tracking-wide">{REQUEST_INFO.footnote}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default RequestInfo;
